import { Add } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import registerClient from '@src/APIs/rest/registerClient';
import { BoxButton, BoxButtons } from '@src/App/Components/Buttons/BoxButton/BoxButton';
import InnerLayout from '@src/App/Components/Core/Layouts/InnerLayout/InnerLayout';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import { captureRegistrationToken } from '@src/App/Utils/registrationToken';
import { stageContext } from '@src/globals';
import { setSecret } from '@store/reducer/clientReducer';
import store, { type AppState } from '@store/store';
import { setOffline } from '@store/storeHelper';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

/**
 * Retrieves the error description from the hash.
 *
 * @returns {string} The error description.
 */
function getErrorDescriptionFromHash(): string | null
{
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const errorDescription = params.get('error_description');
    if (errorDescription)
    {
        return decodeURIComponent(errorDescription);
    }

    return null;
}

/**
 * Represents a component for displaying a logged-in page.
 *
 * @returns {JSX.Element} The rendered JSX element.
 */
function LoggedInPage()
{
    const showErrorDialog = useErrorDialog();
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).loggedin;

    const employee = useSelector((state: AppState) => state.employee);
    const startPage = useSelector((state: AppState) => state.employeeSettings.startPage);

    const [loginError, setLoginError] = React.useState(false as boolean);
    const [redirect, setRedirect] = React.useState('');
    const [companies, setCompanies] = React.useState<null | { title: string; companyId: string }[]>(null);

    const error = getErrorDescriptionFromHash();

    async function checkLogin(companyId?: string)
    {
        console.log('2');
        if (window.location.hash.length > 5)
        {
            console.log('1');
            let idToken = '';
            const cookies = localStorage.getItem('cookies');
            setRedirect(localStorage.getItem('redirect') || '');
            localStorage.clear();
            console.log('Cleared');
            cookies && localStorage.setItem('cookies', cookies);
            store.dispatch(setSecret(null));
            const hashValues = window.location.hash
                .substr(1)
                .split('&')
                .map((s) =>
                {
                    return s.split('=');
                });
            const accessToken = hashValues.find((s) => s[0] === 'access_token');
            const idTokenSplit = hashValues.find((s) => s[0] === 'id_token');
            if (accessToken && idTokenSplit)
            {
                // New ID TOken
                idToken = idTokenSplit[1];
                // Capture the token for the company-registration flow now, while the
                // hash is still fresh — it may no longer be readable after navigation.
                captureRegistrationToken();
                const result = await registerClient(idToken, companyId).catch(async (ex) =>
                {
                    await showErrorDialog(ex, 'Fehler beim Einloggen.');
                });

                if (result)
                {
                    console.log('Login successfull');
                    setOffline(false);
                    if (result === true)
                    {
                        window.location.hash = '';
                    } else
                    {
                        setCompanies(result);
                    }
                    // window.location.pathname = `/${ELinks.PROFILE_EDIT}`;
                } else
                {
                    console.error('Login not successfull');
                    window.location.href = `https://${stageContext.logoutHostname || stageContext.domain}/loginfailed#${window.location.hash}`;
                    throw new Error('Login not successfull');
                }
            }
        }
    }

    useEffect(() =>
    {
        checkLogin().catch((ex) =>
        {
            console.log(ex);
            setLoginError(true);
        });
    }, []);

    useEffect(() =>
    {
        if (employee.id === -1) return;
        if (redirect && redirect.length > 0)
        {
            navigate(redirect);
            return;
        }
        navigate(`/${ELinks[startPage]}`);
    }, [employee.id]);

    if (error)
    {
        return (
            <InnerLayout title={error}>
                <span>{error}</span>
            </InnerLayout>
        );
    }
    if (loginError)
    {
        return (
            <InnerLayout title={langStrings.titleError}>
                <span>{langStrings.textError}</span>
            </InnerLayout>
        );
    }
    if (companies)
    {
        let text: string;
        let buttons: JSX.Element[] = [];
        if (companies.length > 0)
        {
            text = langStrings.selectCompany;
            buttons = companies.map((company) => (
                <BoxButton
                    key={company.companyId}
                    title={company.title}
                    onClick={checkLogin.bind(null, company.companyId)}
                />
            ));
        } else
        {
            text = langStrings.noCompany;
        }
        return (
            <InnerLayout title={langStrings.title}>
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        {text}
                    </Typography>
                </Box>
                <BoxButtons>
                    <BoxButton
                        icon={<Add />}
                        title={langStrings.createCompany}
                        color="secondary"
                        description={
                            companies.length === 0
                                ? 'Get started by creating your company profile'
                                : 'Add another company'
                        }
                        onClick={() => navigate(`/${ELinks.COMPANY_REGISTRATION}`)}
                    />
                    {buttons}
                </BoxButtons>
            </InnerLayout>
        );
    }

    return (
        <InnerLayout title={employee.firstName ? `${langStrings.title} ${employee.firstName}!` : langStrings.title}>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                {langStrings.text}
            </Typography>
        </InnerLayout>
    );
}

export default LoggedInPage;
